import { UserInviteAdminFragment } from "~/gql/queries/users.generated";
import {
	deriveInviteDisplayStatus,
	type InviteStatusSource,
	isInviteSending,
	UserInviteEmailStatusCode
} from "~admin/users/helpers/inviteStatus";

export type UserLoginStatus = "active" | "none" | "pending";

const USER_LOGIN_STATUS_LABELS: Record<UserLoginStatus, string> = {
	active: "Active login",
	none: "No login",
	pending: "Invite pending"
};

export const formatUserLoginStatus = (status: UserLoginStatus): string => USER_LOGIN_STATUS_LABELS[status];

interface LoginStatusUser {
	email: string;
	fusionAuthUserId?: string | null;
}

export const buildPendingInviteEmailSet = (
	invites: (InviteStatusSource & Pick<UserInviteAdminFragment, "recipientEmail">)[]
): Set<string> =>
	new Set(
		invites
			.filter((invite) => deriveInviteDisplayStatus(invite).status !== "expired")
			.map((invite) => invite.recipientEmail.toLowerCase())
	);

export const deriveUserLoginStatus = (user: LoginStatusUser, pendingInviteEmails: Set<string>): UserLoginStatus => {
	if (user.fusionAuthUserId) return "active";
	if (pendingInviteEmails.has(user.email.toLowerCase())) return "pending";
	return "none";
};

export type InviteIdsByEmail = Map<string, string>;

export type InvitesByEmail = Map<string, UserInviteAdminFragment>;

const inviteRecency = (invite: Pick<UserInviteAdminFragment, "createdAt" | "emailSentAt" | "resentAt">): number =>
	new Date(invite.resentAt ?? invite.emailSentAt ?? invite.createdAt).getTime();

export const buildInvitesByEmail = (invites: UserInviteAdminFragment[]): InvitesByEmail =>
	invites.reduce<InvitesByEmail>((byEmail, invite) => {
		const email = invite.recipientEmail.toLowerCase();
		const current = byEmail.get(email);
		if (!current || inviteRecency(invite) >= inviteRecency(current)) byEmail.set(email, invite);
		return byEmail;
	}, new Map());

export const buildInviteIdsByEmail = (invites: UserInviteAdminFragment[]): InviteIdsByEmail =>
	new Map([...buildInvitesByEmail(invites)].map(([email, invite]) => [email, invite.inviteId]));

export type UserInviteAction = { inviteId: string; kind: "resend" } | { kind: "send" };

export interface InviteActionTarget {
	email: string;
	inviteAction: UserInviteAction | null;
	userId: string;
}

export const resolveUserInviteAction = (
	user: LoginStatusUser,
	inviteIdsByEmail: InviteIdsByEmail
): UserInviteAction | null => {
	if (user.fusionAuthUserId) return null;
	const inviteId = inviteIdsByEmail.get(user.email.toLowerCase());
	return inviteId ? { inviteId, kind: "resend" } : { kind: "send" };
};

const resolveResendInvite = (
	user: Pick<InviteActionTarget, "email" | "inviteAction">,
	invitesByEmail: InvitesByEmail
): UserInviteAdminFragment | undefined => {
	if (user.inviteAction?.kind !== "resend") return undefined;
	const invite = invitesByEmail.get(user.email.toLowerCase());
	return invite && invite.inviteId === user.inviteAction.inviteId ? invite : undefined;
};

export const resolveCopySetupLinkInvite = (
	user: Pick<InviteActionTarget, "email" | "inviteAction">,
	invitesByEmail: InvitesByEmail
): UserInviteAdminFragment | undefined => {
	const invite = resolveResendInvite(user, invitesByEmail);
	if (!invite) return undefined;
	const isSent = invite.emailStatus === UserInviteEmailStatusCode.Sent;
	const isLive = deriveInviteDisplayStatus(invite).status === "unused";
	return isSent && isLive ? invite : undefined;
};

export const isUserInviteSending = (
	user: Pick<InviteActionTarget, "email" | "inviteAction">,
	invitesByEmail: InvitesByEmail,
	now: Date = new Date()
): boolean => {
	const invite = resolveResendInvite(user, invitesByEmail);
	return !!invite && isInviteSending(invite, now);
};
